/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Snackbar,
  Backdrop,
  CircularProgress,
  Breadcrumbs,
  Link,
  Container,
  Switch
} from "@mui/material";
import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import "./SavePlan.css";
import Topheader from "../../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationDetailsErrorDialog from "../../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { useForm } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { DataGrid, useGridApiContext } from "@mui/x-data-grid";
import Alert from "@mui/material/Alert";
import EditIcon from "@mui/icons-material/Edit";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";

// LoadingBackdrop Component - Reusable loading indicator
const LoadingBackdrop = ({ open }) => (
  <Backdrop
    sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
    open={open}
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

LoadingBackdrop.propTypes = {
  open: PropTypes.bool.isRequired,
};

function RenderEditIcon({ id, unsavedChangesRef, planId }) {
  const apiRef = useGridApiContext();

  return (
    <>
      {unsavedChangesRef.current.unsavedRows !== id && (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <strong>
            {!planId && (
              <EditIcon
                className="edit-icon"
                style={{ fontSize: '18px' }}
                onClick={() => {
                  apiRef.current.startCellEditMode({
                    id: id,
                    field: "price",
                  });
                  unsavedChangesRef.current.unsavedRows = id;
                }}
              />
            )}
          </strong>
        </div>
      )}
    </>
  );
}

RenderEditIcon.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  unsavedChangesRef: PropTypes.object.isRequired,
  planId: PropTypes.string,
};

function buildPlanPrices(productData, planId, product, data) {
  return productData.map((obj) => ({
    id: planId
      ? product.oneTimePlan.planPrices.find(
          (item) => item.countryCode === obj.countryCode
        ).id
      : null,
    isTaxable: false,
    periodUnit: "DAY",
    price: Number(obj.price).toFixed(2),
    status: "ACTIVE",
    currencyCode: obj.currencyCode,
    countryCode: obj.countryCode,
    country: obj.country,
    taxRateinPercent: obj.taxRateinPercent,
    taxRate: obj.taxRate,
    taxIncluded: false,
    name: data.productId + "_" + obj.countryCode,
    description: data.productId + "_" + obj.countryCode,
  }));
}

function applyPlanTypeSettings(plan, planTypeVal) {
  if (planTypeVal === "RECURRING") {
    plan.chargeType = "IMMEDIATE";
  } else {
    plan.recurringFrequency = null;
    plan.recurringFrequencyUnit = null;
  }
}

function buildNewPlanObject(product, plan, planTypeVal, packageName, sellerId, appId) {
  const newProductBase = {
    productId: packageName,
    status: "ACTIVE",
    productType: "APP",
    resourceType: "NONCONSUMABLE",
    sellerId: sellerId,
    packageId: packageName,
    applicationId: appId,
  };

  if (product.product && product.product.id !== "") {
    const newPlan = product;
    if (planTypeVal === "RECURRING") {
      newPlan.recurringPlans = [plan];
      newPlan.oneTimePlan = null;
    } else {
      newPlan.oneTimePlan = plan;
      newPlan.recurringPlans = null;
    }
    return newPlan;
  }

  if (planTypeVal === "RECURRING") {
    return { product: newProductBase, recurringPlans: [plan] };
  }
  return { product: newProductBase, oneTimePlan: plan };
}

function SavePlan(props) {
  const { appId, productId, planId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.state.teamId;
  const sellerId = location.state.sellerId;
  const product = location.state.product;
  const oneTimeplanExists = location.state.oneTimeplanExists;
  const usedRecurringFrequencies = location.state.usedRecurringFrequencies;
  const [packageName, setPackageName] = useState("");
  const [exchangePriceData, setExchangePriceData] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const { loading, submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "onChange",
  });

  const recurringFrequencyDropdownArray = [
    { key: "1-DAY", value: "Daily" },
    { key: "1-MONTH", value: "Monthly" },
    { key: "3-MONTH", value: "Quarterly" },
    { key: "6-MONTH", value: "Half yearly" },
    { key: "1-YEAR", value: "Yearly" },
  ];

  const errorsApp = useSelector((state) => state.errors);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(true);
  const [countryTableRows, setCountryTableRows] = useState([]);

  useEffect(() => {
    if (oneTimeplanExists) {
      setplanTypeVal("RECURRING");
    }
    if (appData?.submissions && appData.submissions.length > 0) {
      dispatch(fetchSubmissionDetails(appId, appData.submissions[0].id));
    }
  }, [appData]);
  useEffect(() => {
    if (submissionDetails?.binary?.packageName) {
      setPackageName(submissionDetails.binary.packageName);
    }
  }, []);
  const [recurringFrequencyVal, setRecurringFrequencyVal] =
    React.useState("1-DAY");
  useEffect(() => {
    if (planId) {
      APIManager.getPlanDetails(productId, planId).then((json) => {
        if (json.oneTimePlan === null) {
          setplanTypeVal("RECURRING");
          setValue("name", json.recurringPlans[0].name);
          setRecurringFrequencyVal(
            json.recurringPlans[0].recurringFrequency +
              "-" +
              json.recurringPlans[0].recurringFrequencyUnit
          );
          setValue("basePrice", json.recurringPlans[0].basePrice.price);
          setValue("gracePeriod", json.recurringPlans[0].gracePeriod);
          // Append basePrice (USA) at the end of planPrices
          const allPrices = [...json.recurringPlans[0].planPrices, json.recurringPlans[0].basePrice];
          setExchangePriceData(allPrices);
          createDataGrid(allPrices);
        } else {
          setplanTypeVal("ONETIME");
          setValue("name", json.oneTimePlan.name);
          setValue("basePrice", json.oneTimePlan.basePrice.price);
          setValue("gracePeriod", json.oneTimePlan.gracePeriod);
          // Append basePrice (USA) at the end of planPrices
          const allPrices = [...json.oneTimePlan.planPrices, json.oneTimePlan.basePrice];
          setExchangePriceData(allPrices);
          createDataGrid(allPrices);
        }
      });
    }
    setLoader(false);
  }, []);

  const backToApplications = () => {
    navigate(-1);
  };

  const backToAppDetails = () => {
    navigate(`/ui/developer/applications/${appId}`, {
      state: { teamId: teamId, sellerId: sellerId },
    });
  };

  const callNewSubmissionId = (submissionId) => {
    dispatch(fetchSubmissionDetails(appId, submissionId));
  };

  const populatePrice = (event) => {
    setLoader(true);
    let basePrice = event.target.value;
    if (!Number.isNaN(Number.parseFloat(basePrice)) && Number.parseFloat(basePrice) != 0) {
      APIManager.getExchangeRates(basePrice, "USD").then((json) => {
        if (json.currencyData && json.currencyData.length > 0) {
          setExchangePriceData(json.currencyData);
          createDataGrid(json.currencyData);
          setLoader(false);
        }
      });
    }
    if (basePrice === "") {
      setExchangePriceData([]);
      setCountryTableRows([]);
    }
    setLoader(false);
  };
  const createDataGrid = (data) => {
    let countryData = [];
    let count = 1;
    data.forEach((country) => {
      countryData.push({
        id: count,
        country: country.country,
        currencyCode: country.currencyCode,
        price: country.price,
        taxRate:
          country.taxRateinPercent +
          " (" +
          country.currencyCode +
          " " +
          country.taxRate +
          ")",
      });
      count++;
    });
    setCountryTableRows(countryData);
  };

  const checkExistingPlanFrequency = (frequency) => {
    return usedRecurringFrequencies.includes(frequency);
  };

  const countryTableColumns = [
    {
      field: "country",
      headerName: "Country",
      width: 140,
      headerClassName: "country-table-general-header country-header-first",
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '8px' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "currencyCode",
      headerName: "Price",
      align: "right",
      headerAlign: "right",
      sortable: false,
      headerClassName: "country-table-general-header",
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
          <span style={{ color: "#717273" }}>{params.value}</span>
        </div>
      ),
    },
    {
      field: "price",
      headerName: planId ? "" : "(Editable)",
      editable: !planId,
      align: "left",
      headerAlign: "left",
      type: "number",
      width: 80,
      headerClassName: "price-colum-header",
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "editTax",
      headerName: "",
      align: "left",
      renderCell: (params) => (
        <RenderEditIcon id={params.id} unsavedChangesRef={unsavedChangesRef} planId={planId} />
      ),
      sortable: false,
      headerClassName: "country-table-general-header",
    },
    {
      field: "taxRate",
      headerName: "Tax Rate",
      width: 140,
      align: "left",
      headerAlign: "left",
      headerClassName: "country-table-general-header tax-rate-header",
      renderCell: (params) => {
        const parts = params.value.split(' ');
        if (parts.length > 0) {
          parts[0] = parts[0] + '%';
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', paddingLeft: '32px' }}>
            {parts.join(' ')}
          </div>
        );
      },
    },
  ];

  const unsavedChangesRef = React.useRef({
      unsavedRows: "",
    });

  const onSubmit = (data) => {
    setLoader(true);
    const usCountry = exchangePriceData.find((item) => item.countryCode === "USA");
    const productData = exchangePriceData.filter((item) => item.countryCode !== "USA");
    const planPrices = buildPlanPrices(productData, planId, product, data);

    const plan = {
      name: data.name,
      planType: planTypeVal,
      recurringFrequency: planTypeVal === "RECURRING" ? data.recurringFrequency.split("-")[0] : null,
      recurringFrequencyUnit: planTypeVal === "RECURRING" ? data.recurringFrequency.split("-")[1] : null,
      chargeType: "IMMEDIATE",
      autoRenew: true,
      gracePeriod: data.gracePeriod,
      gracePeriodUnit: "DAY",
      planPrices,
      basePrice: {
        productId: product.product && product.product.id !== "" ? product.product.id : packageName,
        price: data.basePrice,
        status: "ACTIVE",
        basePrice: true,
        country: usCountry.country,
        currencyCode: usCountry.currencyCode,
        countryCode: usCountry.countryCode,
        taxRateinPercent: usCountry.taxRateinPercent,
        taxRate: usCountry.taxRate,
        name: data.name + "_" + usCountry.currencyCode,
        description: data.name + "_" + usCountry.currencyCode,
      },
    };

    if (planId) {
      plan.basePrice.id = product.oneTimePlan.basePrice.id;
    }
    applyPlanTypeSettings(plan, planTypeVal);

    const newPlan = buildNewPlanObject(product, plan, planTypeVal, packageName, sellerId, appId);

    if (planId) {
      plan.id = planId;
      updatePlan(newPlan, planId);
    } else {
      savePlan(newPlan);
    }
  };

  const savePlan = (data) => {
    APIManager.savePlanPremium(data).then((json) => {
      setOpenSnackbar(true);
      setSnackMsg(`Added successfully`);
      setTimeout(function () {
        navigate(-1);
      }, 3000);
    });
  };

  const updatePlan = (data, planId) => {
    APIManager.updatePlanPremium(planId, data).then((json) => {
      setOpenSnackbar(true);
      setSnackMsg(`Updated successfully`);
      setTimeout(function () {
        navigate(-1);
      }, 3000);
    });
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, lastUpdated: new Date() };
    const countryObj = exchangePriceData.find(
      (item) => item.country === newRow.country
    );
    let changedPrice = newRow.price;
    let newTaxRate = newRow.price * (countryObj.taxRateinPercent / 100);
    updatedRow.taxRate =
      countryObj.taxRateinPercent +
      " (" +
      countryObj.currencyCode +
      " " +
      newTaxRate.toFixed(2) +
      ")";
    updatedRow.price = Number(changedPrice).toFixed(2);
    exchangePriceData.find(
      (item) => item.country === updatedRow.country
    ).price = updatedRow.price;
    exchangePriceData.find(
      (item) => item.country === updatedRow.country
    ).taxRate = newTaxRate.toFixed(2);
    unsavedChangesRef.current.unsavedRows = "";
    return updatedRow;
  };

  const handleProcessRowUpdateError = (error) => {};
  const [planTypeVal, setplanTypeVal] = React.useState("ONETIME");

  const handleChange = (event) => {
    setplanTypeVal(event.target.value);
  };
  const handleRecurringFrequencyChange = (event) => {
    setRecurringFrequencyVal(event.target.value);
  };

  return (
    <>
      {loader && <LoadingBackdrop open={loader} />}

      <Topheader />
      {
        <Container maxWidth="lg">
                          <Grid container>
                            <Grid items xs={12} sm={12} md={12} lg={12}></Grid>
        <div
          className="container"
          style={
            submissionDetails.status === "DRAFT"
              ? {
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",

                }
              
              : { display: "flex", flexDirection: "row", width: "1260px", }

          }
        >
          <div
            className="side-menu"
            style={
              submissionDetails.status === "DRAFT"
                ? { marginTop: "0px" }
                : { marginTop: "69px" }
            }
          >
            <button
              className="arrow-back"
              onClick={backToApplications}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
            >
              <div>
                <ArrowBackIcon style={{ color: " #717273" }} />
              </div>
              <div className="my-apps">Product Overview</div>
            </button>
            {submissionDetails &&
              Object.keys(submissionDetails).length > 0 &&
              submissionDetails.status !== "DRAFT" &&
              appData &&
              Object.keys(appData).length > 0 && (
                <ApplicationDetailsSideMenu
                  appData={appData}
                  teamId={teamId}
                  isMonetized={submissionDetails.isMonetized}
                  callNewSubmissionId={callNewSubmissionId}
                />
              )}
          </div>
          <div
            className="submission-container"
            style={
              submissionDetails.status === "DRAFT"
                ? { marginLeft: "0" }
                : { width: "89%", marginTop: "12px", marginLeft: "70px" }
            }
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="in-app-title" style={{ marginLeft: "15%" }}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    onClick={backToAppDetails}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackIcon
                      sx={{ mr: 0.5, marginRight: "19px", color: "#b3b3b3" }}
                      fontSize="inherit"
                    />
                    {submissionDetails &&
                      Object.keys(submissionDetails).length > 0 && (
                        <>{submissionDetails.descriptions[0].applicationName}</>
                      )}
                  </Link>
                  <Link
                    underline="hover"
                    onClick={backToApplications}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    App Pricing
                  </Link>
                  <Link
                    underline="none"
                    sx={{ fontWeight: "500", color: "#2a2c2d" }}
                  >
                    {planId ? "Edit Plan" : "Add New Plan"}
                  </Link>
                </Breadcrumbs>
                <Grid container spacing={2}>
                  {planId && (
                    <Grid size={9}>
                      <h2>Edit Plan</h2>
                    </Grid>
                  )}
                  {!planId && (
                    <Grid size={9}>
                      <h2>Add New Plan</h2>
                    </Grid>
                  )}
                  <Grid size={3}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        sx={(theme) => ({
                          backgroundColor: "#e8e8e8",
                          color: "black",
                          textTransform: "none",
                        })}
                        onClick={backToApplications}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        sx={{ textTransform: "none" }}
                        disabled={!isValid || countryTableRows.length === 0}
                        type="submit"
                      >
                        Save
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container">
                <Grid container spacing={2}>
                  <Grid size={4}>Plan Information</Grid>
                  <Grid size={4}>
                    <div>
                      <label className="form-label required" htmlFor="plan-name">Name</label>
                      <TextField
                        {...register("name", {
                          required: "Name is required",
                        })}
                        id="plan-name"
                        disabled={planId}
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        placeholder="Name"
                        variant="outlined"
                      />

                      {errors.name && (
                        <Alert severity="error">{errors.name.message}</Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginTop: "3%" }}>
                  <Grid size={4}></Grid>
                  <Grid size={8}>
                    <FormControl>
                      <FormLabel id="choose-preference-label" className="form-label required">
                        Choose your preference
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="choose-preference-label"
                        name="controlled-radio-buttons-group"
                        row
                        value={planTypeVal}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          disabled={oneTimeplanExists || planId}
                          value="ONETIME"
                          control={<Radio />}
                          label="One Time Purchase"
                        />
                        <FormControlLabel
                          disabled={planId}
                          value="RECURRING"
                          control={<Radio />}
                          label="Recurring"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container-wide">
                <Divider flexItem />
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}>Price Settings</Grid>
                  <Grid size={3}>
                    <div>
                      {planTypeVal === "ONETIME" && (
                        <label className="form-label required" htmlFor="base-price">
                          Base Price
                        </label>
                      )}
                      {planTypeVal === "RECURRING" && (
                        <label className="form-label required" htmlFor="base-price">
                          Recurring Price
                        </label>
                      )}
                      <TextField
                        {...register(
                          "basePrice",
                          {
                            onBlur: (e) => {
                              populatePrice(e);
                            },
                          },
                          { required: "Base Price is required." }
                        )}
                        disabled={planId && planTypeVal === "RECURRING"}
                        id="base-price"
                        type="number"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                USD
                              </InputAdornment>
                            ),
                          },
                        }}
                        style={{ marginBottom: "0", marginLeft: "0" }}
                        fullWidth
                        variant="outlined"
                        helperText="Base Price doesn't include tax"
                      />
                      {errors.basePrice && (
                        <Alert severity="error">
                          {errors.basePrice.message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                  {planTypeVal === "RECURRING" && (
                    <>
                      <Grid size={3}>
                        <div>
                          <label className="form-label required" htmlFor="recurring-frequency">
                            Recurring Frequency
                          </label>
                          <Select
                            {...register("recurringFrequency", {
                              required: "Recurring Frequency is required.",
                            })}
                            labelId="recurring-frequency-label"
                            fullWidth
                            disabled={planId}
                            id="recurring-frequency"
                            value={recurringFrequencyVal}
                            onChange={handleRecurringFrequencyChange}
                          >
                            {recurringFrequencyDropdownArray.map(
                              (frequency) => (
                                <MenuItem
                                  key={frequency.key}
                                  value={frequency.key}
                                  disabled={checkExistingPlanFrequency(
                                    frequency.key
                                  )}
                                >
                                  {frequency.value}
                                </MenuItem>
                              )
                            )}
                          </Select>
                          {errors.recurringFrequency && (
                            <Alert severity="error">
                              {errors.recurringFrequency.message}
                            </Alert>
                          )}
                        </div>
                      </Grid>
                      <Grid size={2.5}>
                        <div>
                          <label className="form-label required" htmlFor="when-to-charge">
                            When To Charge
                          </label>
                          <Select
                            {...register("chargeTime")}
                            labelId="when-to-charge-label"
                            fullWidth
                            disabled={planId}
                            value=""
                            displayEmpty
                            id="when-to-charge"
                          >
                            <MenuItem value="">Immediately</MenuItem>
                          </Select>
                          {errors.chargeTime && (
                            <Alert severity="error">
                              {errors.chargeTime.message}
                            </Alert>
                          )}
                        </div>
                      </Grid>
                    </>
                  )}
                </Grid>
                <br />
                {planTypeVal === "RECURRING" && (
                  <Grid container spacing={2}>
                    <Grid size={3.5}></Grid>
                    <Grid size={3}>
                      <div>
                        <label className="form-label" htmlFor="grace-period">Grace Period</label>
                        <TextField
                          {...register("gracePeriod", {
                            max: {
                              value: 30,
                              message:
                                "Grace period should be less than 30 days.",
                            },
                            min: {
                              value: 0,
                              message: "Invalid grace period",
                            },
                          })}
                          disabled={planId}
                          id="grace-period"
                          type="number"
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  Days
                                </InputAdornment>
                              ),
                            },
                          }}
                          style={{ marginBottom: "0", marginLeft: "0" }}
                          fullWidth
                          variant="outlined"
                          helperText="Max 30 days"
                        />
                        {errors.gracePeriod && (
                          <Alert severity="error">
                            {errors.gracePeriod.message}
                          </Alert>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                )}
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={8.5}>
                    {countryTableRows.length > 0 && (
                      <div
                        style={{ width: "100%", marginBottom: 60 }}
                      >
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontWeight: "bolder" }}
                        >
                          Country specific price setting{" "}
                          <span className="red-star">*</span>
                        </Typography>
                        <Typography
                          variant="caption"
                          gutterBottom
                          sx={{ display: "block", color: "#757575" }}
                        >
                          These prices can be manually changed as per how you
                          want to change
                        </Typography>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                        <DataGrid
                          rows={countryTableRows}
                          columns={countryTableColumns}
                          disableColumnMenu
                          processRowUpdate={processRowUpdate}
                          onProcessRowUpdateError={handleProcessRowUpdateError}
                          pageSizeOptions={[5, 10, 25]}
                          initialState={{
                            pagination: {
                              paginationModel: { page: 0, pageSize: 5 },
                            },
                          }}
                          rowHeight={34}
                          columnHeaderHeight={32}
                          sx={{
                            '& .MuiDataGrid-cell': {
                              padding: '4px 8px',
                              display: 'flex',
                              alignItems: 'center'
                            },
                            '& .MuiDataGrid-columnHeader': {
                              padding: '4px 8px'
                            },
                            '& .country-header-first': {
                              paddingLeft: '16px !important'
                            },
                            '& .MuiDataGrid-columnHeader.tax-rate-header': {
                              paddingLeft: '40px !important'
                            },
                            '& .MuiDataGrid-row': {
                              maxHeight: '34px !important',
                              minHeight: '34px !important'
                            }
                          }}
                          localeText={{
                            MuiTablePagination: {
                              labelRowsPerPage: "Items per page:",
                            },
                          }}
                        />
                        </div>
                      </div>
                    )}
                  </Grid>
                </Grid>
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={8.5}>
                    <div className="monetize-wrapper">
                      <div className="wrapper-diver">
                        <p
                          className="monetize-text"
                          style={{ fontWeight: "bold" }}
                        >
                          Enable Recurrence{" "}
                          <span style={{ color: "red" }}>*</span>
                          <br />
                          <span
                            className="monetize-text-small"
                            style={{ marginLeft: "0" }}
                          >
                            <b>Note: </b>
                            <span style={{ color: "#757575" }}>
                              This setting cannot be undone once this product is
                              published
                            </span>
                          </span>
                        </p>

                        <div>
                          <Switch
                            {...register("enableRecurrence")}
                            disabled
                            checked
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </form>
          </div>
        </div>
        </Grid>
                        </Container>
      }
      {errorsApp?.errorMessage != null &&
        errorsApp.errorType != null && <ApplicationDetailsErrorDialog />}
      {(appData.loading || loading) && <LoadingBackdrop open={loading} />}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
    </>
  );
}

export default SavePlan;
